import type {
  ODataPostProcessedResultsOfTrackItMeasurementInfoDto,
  TrackItMeasurementInfoDto,
} from '@/api/generator';
import { buildOdataQuery } from '@/utils/odata';
import { useLoading } from '@/utils/useLoading';
import {
  ServerSidePaginationOptions,
  useServerSideOdataPagination,
} from '@/utils/useServerSideOdataPagination';
import { debounce } from 'lodash';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ImportedMeasurementOverviewFilterObject } from './types';

const defaultOptions = {
  page: 1,
  itemsPerPage: 20,
  sortBy: [{ key: 'measurementDate', order: 'desc' }],
} satisfies ServerSidePaginationOptions;

type TrackItMeasurementsRequest = (
  query: ReturnType<typeof buildOdataQuery>,
) => Promise<{ result?: ODataPostProcessedResultsOfTrackItMeasurementInfoDto } | undefined>;

export function useTrackItMeasurements(request: TrackItMeasurementsRequest) {
  const filter = ref<ImportedMeasurementOverviewFilterObject>({});
  const measurements = ref<TrackItMeasurementInfoDto[]>([]);
  const itemsLength = ref(0);

  const { query, updateQuery, onUpdateQuery } = useServerSideOdataPagination();

  const {
    action: loadMeasurements,
    loading,
    loadingFirstTime,
  } = useLoading(async () => {
    const response = await request(
      buildOdataQuery({ ...query.value, search: filter.value.search }),
    );

    measurements.value = response?.result?.value ?? [];
    itemsLength.value = response?.result?.odataCount ?? 0;
  }, true);

  function updateOptions(options: ServerSidePaginationOptions) {
    updateQuery({
      ...options,
      sortBy: options.sortBy?.map((sortItem) =>
        sortItem.key === 'importUser' ? { ...sortItem, key: 'importUser.fullName' } : sortItem,
      ),
    });
  }

  onUpdateQuery(loadMeasurements);

  const loadMeasurementsDebounced = debounce(loadMeasurements, 350);
  watch(() => filter.value.search, loadMeasurementsDebounced);
  onBeforeUnmount(loadMeasurementsDebounced.cancel);

  onMounted(() => updateOptions(defaultOptions));

  return {
    defaultOptions,
    filter,
    measurements,
    itemsLength,
    loading,
    loadingFirstTime,
    loadMeasurements,
    updateOptions,
  };
}
